import type { StaffPaymentMethodType } from "services/api/company/staff.service";

export type PaymentMethodValidationError =
  | "empty"
  | "bankAccountFormat"
  | "internationalBankAccountFormat";

export type PaymentMethodValidationResult =
  | { type: "success"; data: string }
  | { type: "failure"; error: PaymentMethodValidationError };

export type PaymentMethodValidationTranslationKey =
  | "Staff.PaymentMethodValueRequired"
  | "Staff.PaymentMethodBankAccountInvalid"
  | "Staff.PaymentMethodInternationalBankAccountInvalid";

export type PaymentMethodHintTranslationKey =
  | "Staff.PaymentMethodValueHintBankAccount"
  | "Staff.PaymentMethodValueHintInternationalBankAccount";

export type PaymentMethodFallbackErrorTranslationKey =
  | "Staff.PaymentMethodValueInvalidBankAccount"
  | "Staff.PaymentMethodInternationalBankAccountInvalid";

export interface PaymentMethodOption {
  label: string;
  value: StaffPaymentMethodType;
}

const BANK_ACCOUNT_REGEX = /^\d{11,15}$/;
const INTERNATIONAL_BANK_ACCOUNT_REGEX = /^[A-Za-z]{2}[A-Za-z0-9]{13,32}$/;

export const PAYMENT_METHOD_OPTIONS: PaymentMethodOption[] = [
  {
    value: "BankAccount",
    label: "Staff.PaymentMethodBankAccount",
  },
  {
    value: "InternationalBankAccount",
    label: "Staff.PaymentMethodInternationalBankAccount",
  },
];

export const normalizePaymentMethodValue = (value: string): string => {
  return value.trim();
};

export const validatePaymentMethodValue = (
  type: StaffPaymentMethodType,
  value: string,
): PaymentMethodValidationResult => {
  if (value.trim().length === 0) {
    return { type: "failure", error: "empty" };
  }

  if (type === "BankAccount") {
    return BANK_ACCOUNT_REGEX.test(value)
      ? { type: "success", data: value }
      : { type: "failure", error: "bankAccountFormat" };
  }

  return INTERNATIONAL_BANK_ACCOUNT_REGEX.test(value)
    ? { type: "success", data: value }
    : { type: "failure", error: "internationalBankAccountFormat" };
};

export const isPaymentMethodValueValidOrEmpty = (
  type: StaffPaymentMethodType,
  value: string,
): boolean => {
  if (value.trim().length === 0) return true;
  return validatePaymentMethodValue(type, value).type === "success";
};

export const toPaymentMethodValidationTranslationKey = (
  error: PaymentMethodValidationError,
): PaymentMethodValidationTranslationKey => {
  if (error === "empty") return "Staff.PaymentMethodValueRequired";
  if (error === "bankAccountFormat") {
    return "Staff.PaymentMethodBankAccountInvalid";
  }
  return "Staff.PaymentMethodInternationalBankAccountInvalid";
};

export const toPaymentMethodHintTranslationKey = (
  type: StaffPaymentMethodType,
): PaymentMethodHintTranslationKey => {
  if (type === "InternationalBankAccount") {
    return "Staff.PaymentMethodValueHintInternationalBankAccount";
  }

  return "Staff.PaymentMethodValueHintBankAccount";
};

export const toPaymentMethodFallbackErrorTranslationKey = (
  type: StaffPaymentMethodType,
): PaymentMethodFallbackErrorTranslationKey => {
  if (type === "InternationalBankAccount") {
    return "Staff.PaymentMethodInternationalBankAccountInvalid";
  }

  return "Staff.PaymentMethodValueInvalidBankAccount";
};
